import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Vehicle from '../interfaces/Vehicle'
import Client from '../interfaces/Client'
import { AppPath } from '../enums/AppPath'
import { VehicleState } from '../enums/DocStates'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'
import { Rule, validateFields } from '../utils/validateForm'
import { useAuth } from '../hooks/useAuth'
import useDealPoster from '../hooks/useDealPoster'
import contactedViaDealTranslate from '../utils/contactedViaDealTranslate'
import { UserProfileDto } from '../modules/users/types/UserProfileDto'
import useUserProfileFetcher from '../modules/users/hooks/useUserProfileFetcher'
import useMessagePoster from '../hooks/useMessagePoster'
import useMessageFetcher from '../hooks/useMessageFetcher'
import { useDealValidation } from '../hooks/useDealValidation'

import SelectUserField from './SelectUserField'
import { DealShort } from './DealEdit'
import { SelectField } from './SelectField'
import { ButtonAction } from './Buttons'
import ClientEdit from './ClientEdit'
import CardOnePage from './CardOnePage'
import { ClientCard } from './ClientCard'
import VehicleCard from './VehicleCard'
import { DuplicateDealModal } from './DuplicateDealModal'

const getContactedViaIni = (via: string) => {
  let contacted = ''
  const aux = via.toUpperCase()

  if (aux === 'YAPO') {
    contacted = 'CONTACT.VIA.YAPO'
  } else if (aux === 'CHILEAUTOS') {
    contacted = 'CONTACT.VIA.CHILEAUTOS'
  } else if (aux.includes('MARKET')) {
    contacted = 'CONTACT.VIA.WEB_PAGE'
  }

  return contacted
}

const DealCreaPage: React.FC<{
  showVehCreaButton?: boolean
  vehicleId?: string
  onCreateDeal?: (id: string) => void
  vehicleDesc?: string
  clientDesc?: string
  //yapo, chileautos, etc
  external?: string
  messageId?: string
}> = ({
  // El siguiente es true cuando viene desde la creacion "pura"
  // es deir, desde Negocios -> Crea5
  // Deberia ser siempre false
  showVehCreaButton = true,
  vehicleId = '',
  onCreateDeal,
  vehicleDesc,
  clientDesc,
  external = '',
  messageId,
}) => {
  const contactedViaIni = getContactedViaIni(external)
  const dealIni = { _id: '', contactedVia: contactedViaIni, profile: '' }

  const [vehicle, setVehicle] = useState<Vehicle | null>(null)
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const { errorClient, fetchClient } = useClientFetcher()
  const [client, setClient] = useState<Client | null>(null)
  const [showVehicleList, setShowVehicleList] = useState<boolean>(false)
  const [showClientList, setShowClientList] = useState<boolean>(false)
  const [showClientCreate, setShowClientCreate] = useState<boolean>(false)
  const { userData } = useAuth()
  const [deal, setDeal] = useState<DealShort | null>(dealIni)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const { postDeal, errorPostDeal } = useDealPoster()
  const [tryVehDesc, setTryVehDesc] = useState<boolean>(false)
  const [users, setUsers] = useState<UserProfileDto[]>([])
  const { fetchUserProfiles, errorUserProfile } = useUserProfileFetcher()
  const { postMessageAssign } = useMessagePoster()
  const { fetchMessageDetail } = useMessageFetcher()
  const { validateDeal } = useDealValidation()

  // Duplicate deal modal state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateData, setDuplicateData] = useState<any>(null)

  const navigate = useNavigate()

  useEffect(() => {
    const fetchDataAsync = async () => {
      const data: UserProfileDto[] = await fetchUserProfiles(userData?.organization?._id ?? '', 0)
      setUsers(data)

      // Si viene un Id de mensaje, entonces lo buscamos para proponer
      // un usuario
      if (messageId) {
        const message = await fetchMessageDetail(messageId)

        if (message && deal) {
          setDeal({ ...deal, profile: message.assignedTo ?? '' })
        }
      }
    }

    fetchDataAsync()
  }, [])

  useEffect(() => {
    if (!vehicleId) {
      setTryVehDesc(true)

      return // Exit early if `vehicleId` is undefined
    }

    const fetchDataAsync = async () => {
      const data = await fetchVehicle(vehicleId)

      if (!data) {
        setTryVehDesc(true)

        return
      }
      setVehicle(data)
    }

    fetchDataAsync()
  }, [vehicleId])

  const onClickVehCard = async (id: string) => {
    if (!id) {
      return // Exit early if vehicleId is undefined
    }
    setShowVehicleList(false)
    const vehicle = await fetchVehicle(id)
    setVehicle(vehicle)
  }
  const onClickCliCard = async (id: string) => {
    if (!id) {
      return // Exit early if vehicleId is undefined
    }
    setShowClientList(false)
    const client = await fetchClient(id)
    setClient(client)
  }
  // "Crear..." en la lista de clientes
  const onCreateCliClick = () => {
    setShowClientList(false)
    setShowClientCreate(true)
  }
  // "Crear" de abajo en la página de creación
  const onReturnCreateCli = async (id: string) => {
    const client = await fetchClient(id)
    setClient(client)

    if (client) {
      setShowClientCreate(false)
    }
  }

  // En los combo box
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { value, name } = e.target

    if (!deal) return
    setDeal({ ...deal, [name]: value })
  }

  // Creamos el Negocio
  const createDealClick = async () => {
    if (!deal) return

    if (!userData) return

    if (getButtonDisabled()) return

    const fieldsToValidate = [
      { name: 'contactedVia', value: deal.contactedVia, rule: Rule.REQUIRED },
      { name: 'profile', value: deal.profile, rule: Rule.REQUIRED },
    ]
    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }
    setErrors({})

    // Validate for duplicate deals
    try {
      const validation = await validateDeal(vehicle!._id, client!._id)

      if (validation.hasDuplicate && validation.existingDeal) {
        setDuplicateData(validation.existingDeal)
        setShowDuplicateModal(true)

        return // Stop execution and wait for user decision
      }
    } catch (error) {
      console.error('Error validating deal:', error)
      // Continue with creation if validation fails
    }

    await createDealAfterValidation()
  }

  const createDealAfterValidation = async () => {
    if (!vehicle || !client || !deal || !userData?.organization) return

    const mydeal = {
      _id: deal._id,
      contactedVia: deal.contactedVia,
      profile: deal.profile,
    }

    // Si viene desde el mensaje, primero lo actualizamos
    // con el vendedor (profile), sin chequear resultado
    if (messageId) {
      await postMessageAssign(messageId, deal.profile)
    }
    const id = await postDeal(mydeal, false, vehicle._id, client._id, userData?.organization._id)

    if (id) {
      if (onCreateDeal) {
        onCreateDeal(id)
      } else {
        const url = `/${AppPath.Deals}/view/${id}`
        navigate(url)
      }
    }
  }
  const searchVehTerm = tryVehDesc ? vehicleDesc : ''
  const searchCliTerm = clientDesc

  const getButtonDisabled = () => {
    // Desabilitamos solo si  existe un vehicle, un client
    // un medio de contacto y un usuario asignado
    return !(vehicle && client && deal?.contactedVia && deal.profile)
  }

  const handleDuplicateConfirm = () => {
    setShowDuplicateModal(false)
    createDealAfterValidation() // Continue with creation
  }

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false)
    setDuplicateData(null)
  }

  return (
    <div>
      <div className="flex flex-col items-center space-y-2">
        <div className="text-2xl">Creando Negocio.</div>
        <div className="flex space-x-4">
          {/* Vehicle Selection */}
          <button
            className="flex items-center justify-center p-4 border rounded-md cursor-pointer hover:bg-gray-100"
            onClick={() => {
              setShowVehicleList(true)
              setShowClientList(false)
            }}
          >
            <VehicleCard vehicle={vehicle} />
          </button>
          {/* Client Selection */}
          <button
            className="flex items-center justify-center p-4 border rounded-md cursor-pointer hover:bg-gray-100"
            onClick={() => {
              setShowClientList(true)
              setShowVehicleList(false)
            }}
          >
            <ClientCard client={client} />
          </button>
        </div>

        {(!client || !vehicle) && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span className="text-xl">ℹ️</span>
            <span>Por favor asigna un vehículo y un cliente primero.</span>
          </div>
        )}
      </div>

      {showVehicleList && (
        <CardOnePage
          docuType={AppPath.Vehicles}
          showButtons={false}
          select={VehicleState.Negociables}
          onClickCard={onClickVehCard}
          showCreaButton={showVehCreaButton}
          searchTermIni={searchVehTerm}
        />
      )}
      {showClientList && (
        <CardOnePage
          docuType={AppPath.Clients}
          showButtons={false}
          select={VehicleState.Negociables}
          onClickCard={onClickCliCard}
          onCreateClick={onCreateCliClick}
          searchTermIni={searchCliTerm}
        />
      )}
      {showClientCreate && <ClientEdit idOverride={true} onReturn={(id) => onReturnCreateCli(id)} />}

      <div>
        <div className="flex justify-center mt-4">
          <SelectField
            label="Medio de contacto"
            name="contactedVia"
            value={deal?.contactedVia}
            options={contactedViaDealTranslate}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        </div>
        <div className="flex justify-center mt-4">
          <SelectUserField
            label="Asignar usuario (nota: solo podrás asignarlo una vez)"
            name="profile"
            value={deal?.profile}
            options={users}
            handleInputChange={handleInputChange}
          />
        </div>
        <div className="flex justify-center mt-4 mb-10">
          <ButtonAction label={'Crear'} handleButtonClick={createDealClick} disabled={getButtonDisabled()} />
        </div>
      </div>
      <div>{errorClient?.message}</div>
      <div>{errorVehicle?.message}</div>
      <div>{errorPostDeal?.message}</div>
      <div>{errorUserProfile?.message}</div>

      {/* Duplicate Deal Modal */}
      {duplicateData && (
        <DuplicateDealModal
          isOpen={showDuplicateModal}
          onConfirm={handleDuplicateConfirm}
          onCancel={handleDuplicateCancel}
          existingDeal={duplicateData}
        />
      )}
    </div>
  )
}
export default DealCreaPage
